/**
 * @file excelReportExporter.js
 * @description Shared Excel exporter that reuses the CSV report definition interface to produce xlsx workbooks.
 */

import { utils, write } from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { buildCsvReportData } from './csvReportExporter';
import { LogEventType } from '../data/enums';
import { logEvent } from '../../services/loggingService';

const EXCEL_MIME_TYPE =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const HEADER_STYLE = {
    font: { bold: true },
};

function replaceExtension(filename, extension) {
    return `${String(filename).replace(/\.[^.]+$/, '')}.${extension}`;
}

function buildColumnWidths(headers, rows) {
    return headers.map((header, columnIndex) => {
        const longestValueLength = rows.reduce(
            (longest, row) => {
                const value = Object.values(row)[columnIndex];
                return Math.max(longest, String(value ?? '').length);
            },
            String(header ?? '').length,
        );

        return { wch: Math.min(longestValueLength + 2, 60) };
    });
}

function applyHeaderStyle(worksheet, headers) {
    headers.forEach((unusedHeader, columnIndex) => {
        const cellRef = utils.encode_cell({ r: 0, c: columnIndex });

        if (worksheet[cellRef]) {
            worksheet[cellRef].s = HEADER_STYLE;
        }
    });
}

export function downloadExcelWithCustomHeaders(
    rows,
    filename,
    headers,
    sheetName = 'Sheet1',
) {
    const worksheetRows = [
        headers,
        ...rows.map(row => Object.values(row).map(value => value ?? '')),
    ];
    const worksheet = utils.aoa_to_sheet(worksheetRows);

    worksheet['!cols'] = buildColumnWidths(headers, rows);
    applyHeaderStyle(worksheet, headers);

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, sheetName);

    const workbookBuffer = write(workbook, {
        bookType: 'xlsx',
        type: 'array',
    });

    saveAs(new Blob([workbookBuffer], { type: EXCEL_MIME_TYPE }), filename);
    logEvent(LogEventType.DataDownloaded, `Report downloaded: ${filename}`);
}

export async function exportExcelReport(reportDefinition, queryParams) {
    const reportData = await buildCsvReportData(reportDefinition, queryParams);

    downloadExcelWithCustomHeaders(
        reportData.rows,
        replaceExtension(reportData.filename, 'xlsx'),
        reportData.headers,
    );
}
